import { Disclosure, Transition } from '@headlessui/react';
import { PlusIcon } from '@heroicons/react/24/solid';
import Image from 'next/image';
import React from 'react';
import { classes } from 'shared';
import hero from '../assets/faqs/hero.jpg';
import { A } from '../components/general/link';
import { WhiteArch } from '../components/img/svgs';
import { Meta } from '../components/meta/meta';

export default function FAQs() {
  return (
    <main>
      <Meta title="FAQs" description="Beds for Kids Corporate Partners" />
      <section className="relative h-[65vh] w-full overflow-hidden">
        <Image
          alt="Beds for Kids Warehouse"
          src={hero}
          className="absolute z-0 h-full w-full object-cover"
          sizes="100vw"
          priority
        />
        <div className="pointer-events-none absolute -bottom-[1px] left-0 right-0">
          <WhiteArch />
        </div>
      </section>
      <section className="bg-white pb-12 text-blue-500">
        <h1 className="mx-auto mt-4 max-w-3xl text-center font-operetta text-4xl md:text-6xl">
          frequently asked questions
        </h1>
        <h2 className="text-center font-caliban text-4xl leading-8 text-green-500 md:text-5xl">
          Have questions?
          <br className="md:hidden" /> We&#39;ve got answers.
        </h2>
      </section>
      <section className="flex justify-center bg-white pb-24 text-blue-500">
        <div className="mx-4 grid max-w-3xl gap-x-12 gap-y-8 md:grid-cols-2 lg:mx-auto">
          <FAQ
            title="How can I volunteer?"
            content="There are many ways you and/or your group can volunteer with Beds for Kids."
            link="/how-to-help/?tab=volunteer"
          />
          <FAQ
            title="Where is Beds for Kids?"
            content={
              <div className="flex flex-col gap-2">
                <p>Beds for Kids is located in Charlotte, North Carolina.</p>
                <p>
                  <i>Mailing Address:</i>
                  <br />
                  1800 Camden Rd. Suite 107-17
                  <br />
                  Charlotte, NC 28203
                </p>
                <p>
                  <i>Warehouse Address:</i>
                  <br />
                  2701-A Hutchison McDonald Road
                  <br />
                  Charlotte, NC 28269
                </p>
              </div>
            }
            link="/contact"
          />
          <FAQ
            title="Are my donations to Beds for Kids tax-deductible?"
            content="Yes, Beds for Kids is a registered 501(c)(3) non-profit as recognized by the IRS. Hard good donors will receive a tax receipt at the time of donation and monetary donors will receive a tax receipt within the first month of the new year."
          />
          <FAQ
            title="If I donate my furniture, who receives it?"
            content="A family with children in the Charlotte area who is in permanent housing and has been referred to Beds for Kids."
          />
          <FAQ
            title="How can I refer a child/family to Beds for Kids?"
            content="All Beds for Kids referrals come from referring agency social workers. Click the following link for additional information on our referring agencies."
            link="/agencies"
          />
          <FAQ
            title="I don't have furniture to donate, how else can I help?"
            content={
              <div className="flex flex-col gap-2">
                <p>
                  <b>Bed Kits: </b>
                  Beds for Kids accepts new bed linens, new pillows and new blankets that are distributed with beds
                  provided.
                </p>
                <A href={'/furniture'} className="font-semibold italic text-green-500">
                  learn more »
                </A>
                <p className="mt-2">
                  <b>Monetary Donations: </b>
                  Beds for Kids is supported by the monetary donations of individuals and organizations.
                </p>
                <A href={'/where-donations-go'} className="font-semibold italic text-green-500">
                  learn more »
                </A>
              </div>
            }
          />
        </div>
      </section>
    </main>
  );
}

export function FAQ({ title, content, link }: { title: string; content: React.ReactNode; link?: string }) {
  return (
    <Disclosure as="div" className="font-avenir text-blue-500">
      {({ open }) => (
        <>
          <dt className="text-lg">
            <Disclosure.Button className="mb-2 flex w-full items-start justify-between border-b-2 border-green-500 pb-2 text-left">
              <h3 className="text-xl font-medium">{title}</h3>
              <span className="ml-6 flex h-7 items-center">
                <PlusIcon
                  className={classes(
                    open ? '-rotate-45' : 'rotate-0',
                    'h-6 w-6 transform transition-transform duration-100',
                  )}
                  aria-hidden="true"
                />
              </span>
            </Disclosure.Button>
          </dt>
          <Transition
            show={open}
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0">
            <Disclosure.Panel as="dd" className="mt-2 flex flex-col gap-2 px-2">
              {typeof content === 'string' ? <p className="text-base">{content}</p> : content}
              {link && (
                <A href={link} className="font-semibold italic text-green-500">
                  learn more »
                </A>
              )}
            </Disclosure.Panel>
          </Transition>
        </>
      )}
    </Disclosure>
  );
}
